import React, { useMemo, useState } from 'react';
import { Tree, Button, Modal, Form, Input, Dropdown, Space, message, Badge, Typography } from 'antd';
import {
    PlusOutlined, MoreOutlined, GlobalOutlined, CheckOutlined, CloseOutlined,
} from '@ant-design/icons';
import MultilingualDialog from '../../../AppService/AppPackage/components/MultilingualDialog';
import {
    findCategory, getCategoryLevel, addCategory, updateCategory, removeCategory, moveCategory,
    ALL_KEY, NONE_KEY,
} from './categoryTreeUtils';

const { Text } = Typography;

const toTreeNodes = (nodes = []) => nodes.map((n) => ({
    key: n.id,
    title: n.name,
    children: (n.children || []).length ? toTreeNodes(n.children) : undefined,
}));

/**
 * 自定义售卖 - 左侧三级分类树（含分类增删改查与拖拽排序）
 */
const SaleCategoryTree = ({ treeData, packages = [], selectedKey, onSelect, onChange }) => {
    const [modal, setModal] = useState({ open: false, mode: 'add', parentId: null, nodeId: null, originName: '' });
    const [i18n, setI18n] = useState({ open: false, originValue: '' });
    const [form] = Form.useForm();

    const treeNodes = useMemo(() => [
        { key: ALL_KEY, title: '所有分类', isLeaf: true },
        { key: NONE_KEY, title: '未分类', isLeaf: true },
        ...toTreeNodes(treeData),
    ], [treeData]);

    const directCount = (id) => packages.filter((p) => p.categoryId === id).length;

    const openAdd = (parentId) => {
        setModal({ open: true, mode: 'add', parentId, nodeId: null, originName: '' });
        form.resetFields();
    };

    const openEdit = (nodeId) => {
        const category = findCategory(treeData, nodeId);
        setModal({ open: true, mode: 'edit', parentId: null, nodeId, originName: category?.name || '' });
        form.setFieldsValue({ name: category?.name, code: category?.code });
    };

    const collectCodes = (nodes, acc = []) => {
        nodes.forEach((n) => {
            acc.push({ id: n.id, code: n.code });
            collectCodes(n.children || [], acc);
        });
        return acc;
    };

    const handleSave = async () => {
        const values = await form.validateFields();
        if (collectCodes(treeData).some((c) => c.code === values.code && c.id !== modal.nodeId)) {
            message.error(`分类编码「${values.code}」已存在，请使用其他编码`);
            return;
        }
        if (modal.mode === 'edit') {
            onChange(updateCategory(treeData, modal.nodeId, values));
            message.success('编辑成功');
        } else {
            const newNode = { id: `c_${Date.now()}`, ...values };
            onChange(modal.parentId ? addCategory(treeData, modal.parentId, newNode) : [...treeData, newNode]);
            message.success('添加成功');
        }
        setModal((m) => ({ ...m, open: false }));
    };

    const handleDelete = (nodeId) => {
        const category = findCategory(treeData, nodeId);
        if (!category) return;
        if ((category.children || []).length) {
            message.warning('该分类下存在子分类，请先删除子分类');
            return;
        }
        if (packages.some((p) => p.categoryId === nodeId)) {
            message.warning('该分类已被套餐引用，不能删除');
            return;
        }
        Modal.confirm({
            title: '删除分类',
            content: `确定删除分类「${category.name}」？该操作将影响 App 端展示。`,
            okText: '确定', cancelText: '取消', okButtonProps: { danger: true },
            onOk: () => {
                onChange(removeCategory(treeData, nodeId));
                if (selectedKey === nodeId) onSelect(NONE_KEY);
                message.success('删除成功');
            },
        });
    };

    const renderTitle = (node) => {
        if (node.key === ALL_KEY) {
            return <span style={{ fontWeight: 600 }}>所有分类（{packages.length}）</span>;
        }
        if (node.key === NONE_KEY) {
            return <span style={{ color: '#909399' }}>未分类（{directCount(null)}）</span>;
        }
        const category = findCategory(treeData, node.key);
        if (!category) return node.title;
        const level = getCategoryLevel(treeData, node.key);
        const count = directCount(node.key);
        const menuItems = [
            { key: 'add', label: '新增子分类', disabled: level >= 3 },
            { key: 'edit', label: '编辑' },
            { key: 'delete', label: '删除', danger: true },
        ];
        const onMenuClick = ({ key, domEvent }) => {
            domEvent.stopPropagation();
            if (key === 'add') openAdd(node.key);
            if (key === 'edit') openEdit(node.key);
            if (key === 'delete') handleDelete(node.key);
        };
        return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{category.name}</span>
                {count > 0 && <Badge count={count} overflowCount={99} style={{ backgroundColor: '#d9d9d9', color: '#666', boxShadow: 'none', fontSize: 11 }} />}
                <Dropdown menu={{ items: menuItems, onClick: onMenuClick }} trigger={['click']}>
                    <MoreOutlined
                        style={{ color: '#909399', padding: '0 2px' }}
                        onClick={(e) => e.stopPropagation()}
                    />
                </Dropdown>
            </span>
        );
    };

    const handleDrop = (info) => {
        const dropKey = info.node.key;
        const dragKey = info.dragNode.key;
        if ([ALL_KEY, NONE_KEY].includes(dropKey) || [ALL_KEY, NONE_KEY].includes(dragKey)) return;
        const dropPos = info.node.pos.split('-');
        const dropPosition = info.dropPosition - Number(dropPos[dropPos.length - 1]);
        const next = moveCategory(treeData, dragKey, dropKey, dropPosition);
        if (next !== treeData) onChange(next);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ fontWeight: 600 }}>套餐分类</span>
                <Button type="link" size="small" icon={<PlusOutlined />} onClick={() => openAdd(null)}>新增一级分类</Button>
            </div>
            <Text type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>最多三级，可拖拽排序</Text>
            <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 6, padding: 8 }}>
                <Tree
                    blockNode
                    showLine={{ showLeafIcon: false }}
                    defaultExpandAll
                    draggable
                    onDrop={handleDrop}
                    treeData={treeNodes}
                    titleRender={renderTitle}
                    selectedKeys={[selectedKey]}
                    onSelect={(keys) => onSelect(keys[0] || NONE_KEY)}
                />
            </div>

            <Modal
                title={modal.mode === 'edit' ? '编辑分类' : '新增分类'}
                open={modal.open}
                onCancel={() => setModal((m) => ({ ...m, open: false }))}
                width={480}
                destroyOnClose
                footer={(
                    <Space>
                        <Button icon={<CloseOutlined />} onClick={() => setModal((m) => ({ ...m, open: false }))}>取消</Button>
                        <Button type="primary" icon={<CheckOutlined />} onClick={handleSave}>保存</Button>
                    </Space>
                )}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        label={(
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>分类名称</span>
                                <Button
                                    type="link" size="small" icon={<GlobalOutlined />} style={{ padding: 0, height: 'auto' }}
                                    onClick={() => setI18n({ open: true, originValue: form.getFieldValue('name') || '' })}
                                >多语言</Button>
                            </div>
                        )}
                        name="name"
                        rules={[{ required: true, message: '请输入分类名称' }, { max: 20, message: '名称长度在 1 到 20 个字符' }]}
                    >
                        <Input placeholder="请输入分类名称" maxLength={20} showCount />
                    </Form.Item>
                    <Form.Item
                        label="分类编码"
                        name="code"
                        rules={[{ required: true, message: '请输入分类编码' }]}
                    >
                        <Input placeholder="请输入分类编码，如 cloud_storage" maxLength={64} showCount />
                    </Form.Item>
                </Form>
            </Modal>

            <MultilingualDialog
                open={i18n.open}
                title="分类名称—多语言翻译"
                originValue={i18n.originValue}
                onCancel={() => setI18n({ open: false, originValue: '' })}
            />
        </div>
    );
};

export default SaleCategoryTree;
